import type { z } from 'zod'
import type { AlgoliaProductType } from '@integration-layer/modules/05.productServices/runtime/components/WrapperProductTile.props.ts'
import { viewItemListSchema } from '@integration-layer/schemas/GAEvents/viewItemList'
import snakeCase from 'lodash/snakeCase'
import { getCarouselNameID } from '@integration-layer/utils/XGenCarouselMap'

type ViewItemList = z.infer<typeof viewItemListSchema>
type BooleanLiteral = 0 | 1

/**
 * A composable for generating Google Analytics 4 view item list events for X-Gen recommendations.
 *
 * Handles tracking of product recommendations from various carousels,
 * including special handling for trending searches.
 *
 * @param {Array<AlgoliaProductType>} XGenRecommendations - Array of product recommendations
 * @param {string} carouselID - Unique identifier for the recommendation carousel
 * @param {Object} [options] - Optional configuration for event tracking
 * @param {boolean} [options.waitForDatalayerSetup=false] - Wait for datalayer to be ready before sending event
 *
 * @returns {void}
 *
 * @throws {Error} Logs errors during event generation or processing
 *
 * @example
 * // Track recommendations for a product carousel
 * useGAxGenViewItemListEvent(recommendedProducts, 'recently_viewed', { waitForDatalayerSetup: true })
 *
 * @remarks
 * - Supports different carousel types (trending searches, brand-specific recommendations)
 * - Handles product availability and size information
 * - Chunks large recommendation lists for better performance
 * - Filters out unavailable or invalid products
 */
export const useGAxGenViewItemListEvent = (
  XGenRecommendations: Array<AlgoliaProductType>,
  carouselID: string,
  options?: { waitForDatalayerSetup: boolean }
) => {
  try {
    // Input validation
    if (
      XGenRecommendations.length === 0 ||
      !Array.isArray(XGenRecommendations)
    ) {
      console.error('Invalid or missing XGenRecommendations array.')
      return
    }

    const { latestSelectedBrand } = useSearchLatestBrandSelection()

    // Get carousel name and type
    const carouselNameID = getCarouselNameID(carouselID)

    const isTrendingSearchesCarousel = carouselNameID.includes(
      'trending_searches_ids_'
    )

    // Transform recommendations into GA-compatible items
    const availableItem = XGenRecommendations.map((item, index) => {
      const { category1, category2, category4 } = extractCategoriesUnlocalized(
        item.hierarchicalCategories || [],
        item.categoryPageId2 || []
      )

      const item_brand =
        item.Brand.toLowerCase() === 'ea7' ? 'EA7' : snakeCase(item.Brand)

      // Find first available size
      const firstAvailableItem = item?.size.find(size => size?.avEcom ?? 0 > 0)

      const selectedSize = firstAvailableItem?.avEcom
        ? {
            size: firstAvailableItem.label,
            quantity: firstAvailableItem?.avEcom > 0 ? 1 : 0,
          }
        : { size: '', quantity: 0 }

      const { pdpPath } = useRouteHelper()

      return {
        affiliation: 'armani.com',
        coupon: '',
        discount: 0 as BooleanLiteral,
        index: index + 1,
        item_availability:
          selectedSize.quantity > 0
            ? (1 as BooleanLiteral)
            : (0 as BooleanLiteral),
        item_brand: item_brand ?? '',
        item_category: snakeCase(category1) ?? '',
        item_category2: snakeCase(category2) ?? '',
        item_category4: snakeCase(category4) ?? '',
        item_discount: 0 as BooleanLiteral,
        item_id: item.objectID ?? '',
        item_img: item.assets?.[0].URL ?? '',
        item_list_id: '',
        item_list_name: snakeCase(`${item.Brand}-${category2}`) ?? '',
        item_mfc_id: item.objectID.replaceAll('_', '') ?? '',
        item_name: item.Name ?? '',
        item_reservable: 0 as BooleanLiteral,
        item_size: selectedSize.size ?? '',
        item_variant: item.ColorDesc ?? '',
        price: item.FullPrice ?? 0,
        quantity: selectedSize.quantity ?? 0,
        item_url: useRequestURL().origin + pdpPath(item),
      }
    }).filter(item => item !== null)

    // Handle different carousel types
    if (!isTrendingSearchesCarousel) {
      // Standard recommendation carousels
      const event: ViewItemList = {
        event: 'view_item_list',
        eventID: '001',
        ecommerce: {
          item_list_id: carouselID ?? '',
          item_list_name: carouselNameID,
          items: availableItem,
        },
      }

      useGASendEvent(viewItemListSchema, event, {
        clearEcommerce: true,
        waitForDatalayerSetup: options?.waitForDatalayerSetup,
      })
    } else if (
      // Trending searches with brand filtering
      carouselNameID.includes(snakeCase(latestSelectedBrand.value)) ||
      latestSelectedBrand.value.includes('all')
    ) {
      // Chunk trending searches for better performance
      const chunkedProductList = chunk(availableItem, 20)
      chunkedProductList.forEach(chunk => {
        const trendingSearchesEvent: ViewItemList = {
          event: 'view_item_list',
          eventID: '001',
          ecommerce: {
            item_list_id: carouselID ?? '',
            item_list_name: carouselNameID,
            items: chunk,
          },
        }

        useGASendEvent(viewItemListSchema, trendingSearchesEvent, {
          clearEcommerce: true,
          waitForDatalayerSetup: options?.waitForDatalayerSetup,
        })
      })
    }
  } catch (error) {
    console.error('Error in useGAViewItemListEvent:', error)
  }
}
